import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

// material-ui
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// assets
import {
  Profile2User,
  Book1,
  Notification,
  Setting2,
  MessageQuestion,
  Profile,
  LogoutCurve,
  Award,
} from "iconsax-react";
import { useNavigate } from "react-router";
import useAuth from "hooks/useAuth";
import { Heart } from "lucide-react";

export default function ProfileTab({ admin, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const { logout, user } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login"); // Navigate to login after successful logout
    } catch (error) {
      console.error("Logout failed:", error);
      // Optionally add error handling with snackbar
      /* 
      openSnackbar({
        open: true,
        message: "Échec de la déconnexion",
        variant: "alert",
        alert: { color: "error" },
        autoHideDuration: 6000,
      });
      */
    }
  };
  const handleListItemClick = (event, index, path) => {
    setSelectedIndex(index);
    navigate(path, {
      state: {
        from: "",
      },
    });
    // Close the dropdown after navigation
    if (onClose) {
      onClose();
    }
  };

  return (
    <List
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        borderRadius: 2,
        p: 2,
        "& .MuiListItemIcon-root": {
          minWidth: 40,
          color: "text.primary",
        },
      }}
    >
      <Typography variant="h6" sx={{ px: 2, pb: 1, fontWeight: 600 }}>
        <FormattedMessage
          id="dashboard.title"
          defaultMessage="Mon Tableau de Bord"
        />
      </Typography>
      <Divider />

      {/* First Section */}
      <ListItemButton
        selected={selectedIndex === 0}
        onClick={(event) =>
          handleListItemClick(event, 0, "/profile/account/basic")
        }
      >
        <ListItemIcon>
          <Profile2User size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.myProfile"
              defaultMessage="Mon profil"
            />
          }
        />
      </ListItemButton>

      <ListItemButton
        selected={selectedIndex === 1}
        onClick={(event) => handleListItemClick(event, 1, "/apprentissage")}
      >
        <ListItemIcon>
          <Book1 size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.myLearning"
              defaultMessage="Mon apprentissage"
            />
          }
        />
      </ListItemButton>

      <ListItemButton
        selected={selectedIndex === 2}
        onClick={(event) => handleListItemClick(event, 2, "/certificats")}
      >
        <ListItemIcon>
          <Award size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.myCertificates"
              defaultMessage="Mes Certificats"
            />
          }
        />
      </ListItemButton>

      <ListItemButton
        selected={selectedIndex === 3}
        onClick={(event) => handleListItemClick(event, 3, "/favorites")}
      >
        <ListItemIcon>
          <Heart size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.myFavorites"
              defaultMessage="Mes favoris"
            />
          }
        />
      </ListItemButton>

      <ListItemButton
        selected={selectedIndex === 4}
        onClick={(event) => handleListItemClick(event, 4, "/newsletter")}
      >
        <ListItemIcon>
          <Notification size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.myNewsletters"
              defaultMessage="Mes newsletters"
            />
          }
        />
      </ListItemButton>

      <Divider sx={{ my: 1 }} />

      {/* Second Section */}
      <ListItemButton
        selected={selectedIndex === 5}
        onClick={(event) =>
          handleListItemClick(
            event,
            5,
            user?.is_verified
              ? "/profile/setting/interests"
              : "/profile/setting/verification"
          )
        }
        sx={
          !user?.is_verified
            ? { "&:hover": { bgcolor: "rgba(255, 193, 7, 0.12)" } }
            : {}
        }
      >
        <ListItemIcon>
          <Setting2 size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.settings"
              defaultMessage="Paramètres"
            />
          }
        />
        {!user?.is_verified && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "#f57c00",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: 900,
              color: "#fff",
              flexShrink: 0,
              ml: "auto",
              "@keyframes breatheWarn": {
                "0%, 100%": {
                  boxShadow: "0 0 3px 1px rgba(245, 124, 0, 0.25)",
                  opacity: 0.65,
                  transform: "scale(0.97)",
                },
                "50%": {
                  boxShadow: "0 0 10px 5px rgba(245, 124, 0, 0.55)",
                  opacity: 1,
                  transform: "scale(1.04)",
                },
              },
              animation: "breatheWarn 2.4s ease-in-out infinite",
            }}
          >
            !
          </Box>
        )}
      </ListItemButton>

      <ListItemButton
        selected={selectedIndex === 6}
        onClick={(event) => {
          event.preventDefault();
          // Redirect to assistance page on landing
          const landingUrl = import.meta.env.VITE_LANDING_URL || `${window.location.protocol}//${window.location.hostname}`;
          const locale = localStorage.getItem('i18nextLng') || 'fr';
          window.location.href = `${landingUrl}/${locale}/assistance`;
        }}
      >
        <ListItemIcon>
          <MessageQuestion size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage id="profile.support" defaultMessage="Support" />
          }
        />
      </ListItemButton>
      {admin && (
        <ListItemButton
          selected={selectedIndex === 7}
          onClick={(event) => handleListItemClick(event, 7, "/admin/dashboard")}
        >
          <ListItemIcon>
            <Profile size={20} />
          </ListItemIcon>
          <ListItemText
            primary={
              <FormattedMessage id="profile.admin" defaultMessage="Admin" />
            }
          />
        </ListItemButton>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Logout Section */}
      <ListItemButton onClick={handleLogout}>
        <ListItemIcon>
          <LogoutCurve size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage
              id="profile.logout"
              defaultMessage="Se déconnecter"
            />
          }
        />
      </ListItemButton>
    </List>
  );
}

ProfileTab.propTypes = {
  admin: PropTypes.bool,
  onClose: PropTypes.func,
};
